import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Spinner } from 'react-bootstrap';
import { faListAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import SimpleBarReact from 'simplebar-react';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import Flex from 'components/common/Flex';
import Pagination from 'components/common/Pagination';
import { showViewEntryDialog } from 'stores/modal';
import { buildRepositoryNotaryJournalColumns } from './repositoryNotaryJournalColumns';
import { useRepositoryNotaryJournals } from './useRepositoryNotaryJournals';

/** Pagination, loading, errors, empty state — use with AdvanceTable as a sibling under AdvanceTableWrapper. */
export const RepositoryNotaryJournalsTableContent = ({
  journals,
  showEmptyState = true,
  pageSizeOptions = [10],
  className
}) => {
  const {
    pageSize,
    setPageSize,
    pageIndex,
    setPageIndex,
    displayEntries,
    totalRecords,
    endOfResults,
    loading,
    initialLoading,
    accessUnavailable,
    error,
    refetch,
    hasSearchParams
  } = journals;

  const hasEntries = displayEntries.length > 0;

  return (
    <div className={className}>
      {!loading && error && accessUnavailable ? (
        <div className="text-center text-600 py-5 px-3">
          <FontAwesomeIcon
            className="text-muted mb-3"
            icon={faListAlt}
            size="3x"
          />
          <p className="fw-semi-bold mb-1">No journal entries available</p>
          <p className="fs--1 mb-0">{error}</p>
        </div>
      ) : null}

      {error && !accessUnavailable ? (
        <div className="alert alert-warning mb-3">
          {error}
          <br />
          <Button className="mt-2" variant="warning" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      ) : null}

      {initialLoading ? (
        <Flex justifyContent="center" className="py-3">
          <Spinner animation="border" size="sm" />
        </Flex>
      ) : null}

      {!error && !initialLoading && (totalRecords > 0 || hasEntries) && (
        <Pagination
          pageIndex={pageIndex}
          pageSize={pageSize}
          totalRecords={totalRecords}
          currentCount={displayEntries.length}
          onPageChange={setPageIndex}
          onPageSizeChange={setPageSize}
          endOfResults={endOfResults}
          disabled={loading}
          loading={loading}
          onRefresh={() => refetch()}
          pageSizeOptions={pageSizeOptions}
          className="px-1"
        />
      )}

      {showEmptyState &&
        !loading &&
        !error &&
        !hasEntries &&
        !hasSearchParams() && (
          <div className="text-center py-5">
            <FontAwesomeIcon
              className="text-muted"
              icon={faListAlt}
              size="3x"
            />
            <p className="my-3 text-600 fs--1 mb-0">
              No journal entries have been shared yet.
            </p>
          </div>
        )}
    </div>
  );
};

RepositoryNotaryJournalsTableContent.propTypes = {
  journals: PropTypes.object.isRequired,
  showEmptyState: PropTypes.bool,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  className: PropTypes.string
};

const repositoryNotaryJournalAdvanceTable = (loading, scrollMaxHeight) => (
  <SimpleBarReact
    style={scrollMaxHeight ? { maxHeight: scrollMaxHeight } : undefined}
  >
    <AdvanceTable
      table
      headerClassName="bg-200 text-900 text-nowrap align-middle"
      rowClassName="align-middle white-space-nowrap"
      tableProps={{
        size: 'sm',
        striped: true,
        className: `fs--1 mb-0 overflow-hidden${
          loading ? ' refreshing-skeleton' : ''
        }`
      }}
    />
  </SimpleBarReact>
);

const RepositoryNotaryJournalsList = ({
  userGuid,
  skip = false,
  viewEntrySource = 'admin-notary-user-journals',
  showEmptyState = true,
  scrollMaxHeight,
  className,
  pageSizeOptions = [10]
}) => {
  const dispatch = useDispatch();
  const { userMaster } = useSelector(state => state.app);

  const journals = useRepositoryNotaryJournals({
    userGuid,
    skip,
    defaultPageSize: pageSizeOptions[0] ?? 10
  });

  const { pageSize, displayEntries, loading, refetch } = journals;

  const onRowClicked = entry => {
    if (!entry?.GUID) return;
    dispatch(
      showViewEntryDialog(
        entry.GUID,
        res => {
          if (res) refetch();
        },
        viewEntrySource
      )
    );
  };

  const columns = useMemo(
    () =>
      buildRepositoryNotaryJournalColumns({
        onRowClicked,
        privacyEnabled: !!userMaster?.PrivacyEnabled
      }),
    [userMaster?.PrivacyEnabled, viewEntrySource, refetch]
  );

  return (
    <div className={className}>
      <AdvanceTableWrapper
        columns={columns}
        data={displayEntries}
        selection={false}
        sortable
        pagination
        perPage={pageSize}
        sortBy={[]}
      >
        <RepositoryNotaryJournalsTableContent
          journals={journals}
          showEmptyState={showEmptyState}
          pageSizeOptions={pageSizeOptions}
        />
        {displayEntries.length > 0
          ? repositoryNotaryJournalAdvanceTable(loading, scrollMaxHeight)
          : null}
      </AdvanceTableWrapper>
    </div>
  );
};

RepositoryNotaryJournalsList.propTypes = {
  userGuid: PropTypes.string,
  skip: PropTypes.bool,
  viewEntrySource: PropTypes.string,
  showEmptyState: PropTypes.bool,
  scrollMaxHeight: PropTypes.string,
  className: PropTypes.string,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number)
};

RepositoryNotaryJournalsList.defaultProps = {
  userGuid: '',
  skip: false,
  viewEntrySource: 'admin-repository-journals',
  showEmptyState: true,
  className: '',
  pageSizeOptions: [10]
};

export { useRepositoryNotaryJournals };
export { buildRepositoryNotaryJournalColumns };
export default RepositoryNotaryJournalsList;
